import React, { useMemo, useState } from 'react';
import { ArrowLeft, AlertTriangle, CheckCircle2, Info } from 'lucide-react';
import './ClosingStockCheck.css';

/**
 * ClosingStockCheck
 * ---------------------------------------------------------------------------
 * Final confirmation gate shown AT THE END of Closing Stock entry — after the
 * user has entered closing stock, and BEFORE the values are committed into the
 * app. Values are saved only once all checks are confirmed.
 *
 * NOTE: Standalone preview screen. Not yet wired into ClosingStockPage.
 * Local state only, no API calls. Pass onBack / onConfirm to hook it in;
 * onConfirm receives the full payload and is where you commit the values.
 *
 * Checks captured:
 *   1. Quantity & price confirmation — every qty and price confirmed and
 *      SIGNED by BOTH the Owner and the Store Manager (name + auto timestamp).
 *   2. Saved preparations — detailed check (Yes / No + remarks).
 *   3. Closing stock timing — taken "Before sale" or "After closing time";
 *      the TSR and closing stock kept for the day follow from this choice.
 *
 * Interactivity: live "X of 3 complete" progress; the "Confirm & Save to App"
 * button stays disabled until all three checks are done.
 */

const TOTAL_CHECKS = 3;

function formatStamp(d) {
  if (!d) return '';
  return d.toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function SignRow({ role, value, at, onChange }) {
  return (
    <div className="csc-sign-row">
      <span className="csc-sign-role">{role} sign-off</span>
      <input
        type="text"
        className="csc-input"
        placeholder={`${role} — type full name to sign`}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
      {at && (
        <div className="csc-stamp">
          <CheckCircle2 size={16} color="#157347" />
          <span>
            Signed by <strong>{value.trim()}</strong> · <strong>{formatStamp(at)}</strong>
          </span>
        </div>
      )}
    </div>
  );
}

function ClosingStockCheck({ onBack, onConfirm }) {
  // 1. Quantity & price confirmation — dual sign-off
  const [ownerSign, setOwnerSign] = useState('');
  const [ownerAt, setOwnerAt] = useState(null);
  const [storeSign, setStoreSign] = useState('');
  const [storeAt, setStoreAt] = useState(null);

  // 2. Saved preparations — detailed check
  const [prepChecked, setPrepChecked] = useState(null); // 'Yes' | 'No'
  const [prepRemarks, setPrepRemarks] = useState('');

  // 3. Closing stock timing
  const [timing, setTiming] = useState(null); // 'Before sale' | 'After closing time'

  const signSetter = (setName, setAt, curAt) => (value) => {
    setName(value);
    if (value.trim() && !curAt) setAt(new Date());
    if (!value.trim()) setAt(null);
  };

  const status = useMemo(
    () => ({
      qtyPriceSignoff: ownerSign.trim().length > 0 && storeSign.trim().length > 0,
      preparations: prepChecked !== null,
      timing: timing !== null,
    }),
    [ownerSign, storeSign, prepChecked, timing]
  );

  const completed = useMemo(
    () => Object.values(status).filter(Boolean).length,
    [status]
  );
  const allComplete = completed === TOTAL_CHECKS;
  const pct = Math.round((completed / TOTAL_CHECKS) * 100);

  const payload = useMemo(
    () => ({
      qtyPriceConfirmedBy: {
        owner: { name: ownerSign.trim(), at: ownerAt ? ownerAt.toISOString() : null },
        storeManager: { name: storeSign.trim(), at: storeAt ? storeAt.toISOString() : null },
      },
      savedPreparationsCheckedInDetail: prepChecked,
      savedPreparationsRemarks: prepRemarks.trim(),
      closingStockTiming: timing, // 'Before sale' | 'After closing time'
    }),
    [ownerSign, ownerAt, storeSign, storeAt, prepChecked, prepRemarks, timing]
  );

  const handleConfirm = () => {
    if (!allComplete) return;
    if (typeof onConfirm === 'function') onConfirm(payload);
    // eslint-disable-next-line no-console
    else console.log('Closing stock check payload (commit values now):', payload);
  };

  return (
    <div className="csc-page">
      {/* Header */}
      <header className="csc-header">
        <div className="csc-shell csc-header__inner">
          <div className="csc-header-left">
            <button
              type="button"
              className="csc-back-btn"
              aria-label="Back"
              onClick={onBack || undefined}
            >
              <ArrowLeft size={22} />
            </button>
            <h1>Closing Stock Check</h1>
          </div>
          <p className="csc-subtitle">
            Final confirmation · values are saved only after all checks pass
          </p>

          <div className="csc-progress">
            <div className="csc-progress-top">
              <span className="csc-progress-label">Confirmation progress</span>
              <span className={`csc-progress-count ${allComplete ? 'is-done' : ''}`}>
                {completed} of {TOTAL_CHECKS} complete
              </span>
            </div>
            <div
              className="csc-progress-track"
              role="progressbar"
              aria-valuenow={completed}
              aria-valuemin={0}
              aria-valuemax={TOTAL_CHECKS}
            >
              <div
                className={`csc-progress-fill ${allComplete ? 'is-done' : ''}`}
                style={{ width: `${pct}%` }}
              />
            </div>
          </div>
        </div>
      </header>

      {/* Body */}
      <div className="csc-shell csc-body">
        <div className="csc-section-label">Closing Stock Verification</div>

        {/* 1. Quantity & price confirmation — dual sign-off */}
        <section className="csc-card">
          <div className="csc-card__title">
            <span className="csc-card__num">1</span>Quantity &amp; price confirmation
          </div>
          <p className="csc-card__help">
            Every quantity and price must be confirmed and signed by BOTH the
            Owner and the Store Manager.
          </p>
          <SignRow
            role="Owner"
            value={ownerSign}
            at={ownerAt}
            onChange={signSetter(setOwnerSign, setOwnerAt, ownerAt)}
          />
          <SignRow
            role="Store Manager"
            value={storeSign}
            at={storeAt}
            onChange={signSetter(setStoreSign, setStoreAt, storeAt)}
          />
        </section>

        {/* 2. Saved preparations — detailed check */}
        <section className="csc-card">
          <div className="csc-card__title">
            <span className="csc-card__num">2</span>Saved preparations — detailed check
          </div>
          <p className="csc-card__help">
            Have all saved preparations been checked in detail?
          </p>
          <div className="csc-seg" role="group" aria-label="Preparations checked">
            {['Yes', 'No'].map((opt) => (
              <button
                key={opt}
                type="button"
                className={`csc-seg-btn ${prepChecked === opt ? 'is-active' : ''}`}
                aria-pressed={prepChecked === opt}
                onClick={() => setPrepChecked(opt)}
              >
                {opt}
              </button>
            ))}
          </div>
          {prepChecked === 'No' && (
            <div className="csc-note csc-note--danger" role="alert">
              <AlertTriangle size={16} />
              <span>
                Preparations not fully checked — review saved preparations before
                saving, or closing stock may be inaccurate.
              </span>
            </div>
          )}
          <textarea
            className="csc-input"
            style={{ marginTop: 12 }}
            placeholder="Remarks — what was checked in the preparations (optional)"
            value={prepRemarks}
            onChange={(e) => setPrepRemarks(e.target.value)}
          />
        </section>

        {/* 3. Closing stock timing */}
        <section className="csc-card">
          <div className="csc-card__title">
            <span className="csc-card__num">3</span>Closing stock timing
          </div>
          <p className="csc-card__help">
            When was this closing stock taken? Only the TSR and closing stock of
            that day are kept, based on this.
          </p>
          <div className="csc-seg" role="group" aria-label="Closing stock timing">
            {['Before sale', 'After closing time'].map((opt) => (
              <button
                key={opt}
                type="button"
                className={`csc-seg-btn ${timing === opt ? 'is-active' : ''}`}
                aria-pressed={timing === opt}
                onClick={() => setTiming(opt)}
              >
                {opt}
              </button>
            ))}
          </div>
          {timing === 'After closing time' && (
            <div className="csc-note csc-note--info" role="note">
              <Info size={16} />
              <span>
                Taken after closing time — today's TSR and today's closing stock
                are kept together for this day.
              </span>
            </div>
          )}
          {timing === 'Before sale' && (
            <div className="csc-note csc-note--info" role="note">
              <Info size={16} />
              <span>
                Taken before sale — this reading is treated as the opening for the
                day; the TSR and closing stock are paired to that day accordingly.
              </span>
            </div>
          )}
        </section>

        {/* Footer actions */}
        <div className="csc-footer">
          <button
            type="button"
            className="csc-btn csc-btn--secondary"
            onClick={onBack || undefined}
          >
            Back
          </button>
          <button
            type="button"
            className="csc-btn csc-btn--primary"
            onClick={handleConfirm}
            disabled={!allComplete}
            title={
              allComplete
                ? 'Confirm and save closing stock values to the app'
                : `Complete all ${TOTAL_CHECKS} checks to save`
            }
          >
            Confirm &amp; Save to App
          </button>
        </div>
        {!allComplete && (
          <p className="csc-hint">
            {TOTAL_CHECKS - completed} check
            {TOTAL_CHECKS - completed === 1 ? '' : 's'} left — values are entered
            into the app only after all are confirmed.
          </p>
        )}
      </div>
    </div>
  );
}

export default ClosingStockCheck;
